import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/seo/SEO";
import { Helmet } from "react-helmet-async";
import { SkipLink } from "@/components/common/SkipLink";
import { StickyCTA } from "@/components/common/StickyCTA";
import { WhatsAppButton } from "@/components/common/WhatsAppButton";
import { CookieConsent } from "@/components/common/CookieConsent";
import { Toaster } from "@/components/ui/toaster";

type AboutSection = {
  title: string;
  body?: string[];
  highlights?: string[];
  steps?: string[];
  subtitle?: string;
  pillars?: { title: string; bullets: string[] }[];
  groups?: { title: string; bullets: string[] }[];
};

export const AboutPage = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.dir(i18n.language);
  }, [i18n, i18n.language]);

  useEffect(() => {
    const hash = location.hash?.replace("#", "");

    if (hash) {
      const timer = window.setTimeout(() => {
        const target = document.getElementById(hash);
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);

      return () => window.clearTimeout(timer);
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.hash]);

  const getSection = <T extends AboutSection>(key: string) =>
    t(`about.sections.${key}`, { returnObjects: true }) as T;

  const whoWeAre = getSection("who_we_are");
  const howWeWork = getSection("how_we_work");
  const mission = getSection("mission");
  const vision = getSection("vision");
  const values = getSection("values");
  const visionPillars = vision.pillars || [];
  const visionPrimary = visionPillars.slice(0, 2);
  const visionOverflow = visionPillars.slice(2);
  const valuesGroups = [
    ...((values.groups || [])),
    ...visionOverflow.map((pillar) => ({ title: pillar.title, bullets: pillar.bullets }))
  ];
  const missionVisionHeading = t("about.missionVisionHeading");
  const sectionLinks = [
    { id: "who-we-are", label: whoWeAre.title },
    { id: "how-we-work", label: howWeWork.title },
    { id: "mission", label: mission.title },
    { id: "vision", label: vision.title },
    { id: "values", label: values.title },
  ];

  return (
    <div className="bg-neutral-50 min-h-screen text-neutral-900 flex flex-col">
      <SkipLink />
      <SEO title={t("about.title")} description={t("about.intro")} url="/kurumsal" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.insorganizasyon.org",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: t("about.title"),
                item: "https://www.insorganizasyon.org/kurumsal",
              },
            ],
          })}
        </script>
      </Helmet>
      <Header />
      <main id="main-content" className="flex-grow">
        <section className="pt-32 pb-20 bg-gradient-to-b from-white via-white/80 to-brand-beige/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="flex flex-col gap-6 text-center md:text-left rtl:md:text-right">
              <Link
                to="/"
                className="inline-flex items-center justify-center md:justify-start gap-2 text-sm font-medium text-brand-turquoise hover:text-teal-700 transition"
              >
                {t("about.back")}
              </Link>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-neutral-900">
                  {t("about.title")}
                </h1>
                <p className="mt-4 text-lg md:text-xl text-neutral-700 max-w-3xl">
                  {t("about.intro")}
                </p>
              </div>
              <nav className="mt-4 flex flex-wrap justify-center md:justify-start gap-3">
                {sectionLinks.map((link) => (
                  <a
                    key={link.id}
                    href={`#${link.id}`}
                    className="rounded-full border border-brand-turquoise/30 bg-white/70 px-4 py-2 text-sm font-medium text-brand-turquoise shadow-sm transition hover:border-brand-turquoise hover:bg-brand-turquoise/10"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl space-y-16">
            <div id="who-we-are" className="grid gap-10 lg:grid-cols-3">
              <article className="lg:col-span-2 rounded-3xl border border-neutral-200 bg-white/90 p-8 shadow-sm backdrop-blur">
                <h2 className="text-3xl font-semibold text-neutral-900">
                  {whoWeAre.title}
                </h2>
                <div className="mt-6 space-y-4 text-base leading-relaxed text-neutral-700">
                  {(whoWeAre.body || []).map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </article>
              <aside className="rounded-3xl border border-brand-turquoise/30 bg-brand-turquoise/5 p-6 shadow-sm">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-brand-turquoise">
                  {t("about.sections.who_we_are.title")}
                </h3>
                <ul className="mt-4 space-y-3 text-sm font-medium text-neutral-800">
                  {(whoWeAre.highlights || []).map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-brand-turquoise" aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </aside>
            </div>

            <div id="how-we-work" className="rounded-3xl border border-neutral-200 bg-white/90 p-8 shadow-sm backdrop-blur">
              <h2 className="text-3xl font-semibold text-neutral-900">
                {howWeWork.title}
              </h2>
              <ol className="mt-8 space-y-6">
                {(howWeWork.steps || []).map((step, index) => (
                  <li key={index} className="flex gap-4">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-turquoise/15 text-lg font-semibold text-brand-turquoise">
                      {index + 1}
                    </span>
                    <p className="text-base leading-relaxed text-neutral-700">{step}</p>
                  </li>
                ))}
              </ol>
            </div>

            <div id="mission" className="space-y-8">
              <div className="flex flex-col gap-2 text-center">
                <p className="text-sm font-semibold uppercase tracking-wider text-brand-turquoise">
                  {missionVisionHeading}
                </p>
                <h2 className="text-3xl font-semibold text-neutral-900">
                  {mission.title}
                  {mission.subtitle ? (
                    <span className="block text-base font-medium text-neutral-500">{mission.subtitle}</span>
                  ) : null}
                </h2>
              </div>
              <div className="grid gap-8 lg:grid-cols-2">
                {(mission.pillars || []).map((pillar, index) => (
                  <article
                    key={pillar.title}
                    className="rounded-3xl border border-neutral-200 bg-white/90 p-8 shadow-sm backdrop-blur"
                  >
                    <h3 className="text-xl font-semibold text-neutral-900">
                      {index + 1}. {pillar.title}
                    </h3>
                    <ul className="mt-4 space-y-3 text-base text-neutral-700">
                      {pillar.bullets.map((bullet, bulletIndex) => (
                        <li key={bulletIndex} className="flex gap-3">
                          <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-brand-turquoise" aria-hidden />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>

            <div id="vision" className="space-y-8">
              <div className="flex flex-col gap-2 text-center">
                <h2 className="text-3xl font-semibold text-neutral-900">
                  {vision.title}
                  {vision.subtitle ? (
                    <span className="block text-base font-medium text-neutral-500">{vision.subtitle}</span>
                  ) : null}
                </h2>
              </div>
              <div className="grid gap-8 lg:grid-cols-2">
                {visionPrimary.map((pillar, index) => (
                  <article
                    key={pillar.title}
                    className="rounded-3xl border border-neutral-200 bg-white/90 p-8 shadow-sm backdrop-blur"
                  >
                    <h3 className="text-xl font-semibold text-neutral-900">
                      {index + 1}. {pillar.title}
                    </h3>
                    <ul className="mt-4 space-y-3 text-base text-neutral-700">
                      {pillar.bullets.map((bullet, bulletIndex) => (
                        <li key={bulletIndex} className="flex gap-3">
                          <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-brand-turquoise" aria-hidden />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>

            <div id="values" className="grid gap-8 lg:grid-cols-2">
              {valuesGroups.map((group) => (
                <article
                  key={group.title}
                  className="rounded-3xl border border-neutral-200 bg-gradient-to-br from-white via-white to-brand-beige/40 p-8 shadow-sm backdrop-blur"
                >
                  <h2 className="text-2xl font-semibold text-neutral-900">
                    {group.title}
                  </h2>
                  <ul className="mt-5 space-y-3 text-base text-neutral-700">
                    {group.bullets.map((bullet, index) => (
                      <li key={index} className="flex gap-3">
                        <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-brand-turquoise" aria-hidden />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <CookieConsent />
      <Toaster />
      <StickyCTA />
      <WhatsAppButton />
    </div>
  );
};

export default AboutPage;
